/**
 * Cancel Sale Modal
 * Handles cancellation of in-progress sales and refund (estorno) of finalized sales.
 * Requires manager password for authorization.
 */
import { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Lock, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '@/lib/api';

interface CancelSaleModalProps {
  isOpen: boolean;
  saleId: string | null;
  saleStatus: 'em_andamento' | 'finalizada';
  saleNumber?: number;
  onClose: () => void;
  onSuccess: () => void;
}

export function CancelSaleModal({
  isOpen,
  saleId,
  saleStatus,
  saleNumber,
  onClose,
  onSuccess,
}: CancelSaleModalProps) {
  const [reason, setReason] = useState('');
  const [managerPassword, setManagerPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [authError, setAuthError] = useState('');

  const isRefund = saleStatus === 'finalizada';
  const title = isRefund ? 'Estorno de Venda' : 'Cancelar Venda';
  const description = isRefund
    ? `Estornar venda #${saleNumber || ''}? Os itens serão devolvidos ao estoque.`
    : `Cancelar venda #${saleNumber || ''} em andamento?`;

  const resetForm = useCallback(() => {
    setReason('');
    setManagerPassword('');
    setLoading(false);
    setAuthError('');
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [onClose, resetForm]);

  const handleConfirm = useCallback(async () => {
    if (!saleId) return;

    if (!reason.trim()) {
      toast.error('Informe o motivo do cancelamento');
      return;
    }

    if (!managerPassword) {
      toast.error('Senha do gerente é obrigatória');
      return;
    }

    setLoading(true);
    setAuthError('');

    try {
      const endpoint = isRefund
        ? `/api/sales/${saleId}/refund`
        : `/api/sales/${saleId}/cancel`;

      const response = await apiFetch(endpoint, {
        method: 'POST',
        body: JSON.stringify({
          reason,
          manager_password: managerPassword,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.error?.includes('Senha do gerente')) {
          setAuthError('Senha do gerente inválida');
          setLoading(false);
          return;
        }
        toast.error(data.error || 'Erro ao processar cancelamento');
        setLoading(false);
        return;
      }

      toast.success(
        isRefund
          ? `Venda #${saleNumber} estornada com sucesso`
          : `Venda #${saleNumber} cancelada com sucesso`,
      );
      onSuccess();
      handleClose();
    } catch {
      toast.error('Erro de conexão');
      setLoading(false);
    }
  }, [saleId, reason, managerPassword, isRefund, saleNumber, onSuccess, handleClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <motion.div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
          />
          <motion.div
            className="relative z-10 w-full max-w-md rounded-xl border border-border bg-card p-6 shadow-xl"
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
          >
            <button
              onClick={handleClose}
              className="absolute right-4 top-4 rounded-sm p-1 text-muted-foreground hover:text-foreground"
              aria-label="Fechar"
            >
              <X className="h-4 w-4" />
            </button>

            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-destructive/10">
                <AlertTriangle className="h-5 w-5 text-destructive" />
              </div>
              <div>
                <h2 className="text-lg font-semibold">{title}</h2>
                <p className="text-sm text-muted-foreground">{description}</p>
              </div>
            </div>

            <div className="mt-5 space-y-4">
              {/* Reason */}
              <div>
                <label className="mb-1.5 block text-sm font-medium text-foreground">
                  Motivo *
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Informe o motivo do cancelamento..."
                  rows={2}
                  className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                  autoFocus
                />
              </div>

              {/* Manager Password */}
              <div>
                <label className="mb-1.5 flex items-center gap-1.5 text-sm font-medium text-foreground">
                  <Lock className="h-3.5 w-3.5" />
                  Senha do Gerente *
                </label>
                <input
                  type="password"
                  value={managerPassword}
                  onChange={(e) => {
                    setManagerPassword(e.target.value);
                    setAuthError('');
                  }}
                  placeholder="Digite a senha do gerente"
                  className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
                {authError && (
                  <p className="mt-1 text-xs text-destructive">{authError}</p>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={handleClose}
                  className="flex-1 rounded-lg border border-border px-4 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted"
                >
                  Voltar
                </button>
                <button
                  onClick={handleConfirm}
                  disabled={loading || !reason.trim() || !managerPassword}
                  className="flex-1 rounded-lg bg-destructive px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-destructive/90 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {loading ? 'Processando...' : isRefund ? 'Confirmar Estorno' : 'Confirmar Cancelamento'}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
